import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Building2, Bell, Save, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const Field = ({ label, value, onChange, testid, placeholder, type = "text" }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder} className="h-10 rounded-xl" />
  </div>
);

const Card = ({ icon: Icon, title, desc, children, onSave, saving, testid }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testid}>
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Icon className="w-5 h-5 text-sky-600" /> {title}</h3>
        <p className="text-xs text-slate-500 mt-1">{desc}</p>
      </div>
      <Button size="sm" onClick={onSave} disabled={saving} data-testid={`${testid}-save`} className="rounded-xl bg-sky-600 hover:bg-sky-500 text-white">
        <Save className="w-4 h-4 mr-1" /> Save
      </Button>
    </div>
    {children}
  </div>
);

export const SettingsTab = () => {
  const { refresh } = useSettings();
  const [bank, setBank] = useState(null);
  const [company, setCompany] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    api.get("/settings/all").then((r) => {
      setBank(r.data.bank_details || {});
      setCompany({ hours: [], socials: {}, ...(r.data.company || {}) });
      setAlerts(r.data.alerts || {});
    }).catch(() => toast.error("Could not load settings"));
  }, []);

  const save = async (key, value) => {
    setSaving(key);
    try {
      await api.put(`/settings/${key}`, { value });
      toast.success("Saved — live on the website");
      refresh();
    } catch { toast.error("Save failed"); } finally { setSaving(""); }
  };

  if (!bank || !company || !alerts) return <div className="text-slate-400 text-sm">Loading settings…</div>;

  const setC = (k, v) => setCompany({ ...company, [k]: v });
  const setSocial = (k, v) => setCompany({ ...company, socials: { ...company.socials, [k]: v } });
  const setHour = (i, k, v) => setCompany({ ...company, hours: company.hours.map((h, j) => (j === i ? { ...h, [k]: v } : h)) });

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card icon={Building2} title="Contact & Company Info" desc="Shown in the navbar, footer and order page." onSave={() => save("company", company)} saving={saving === "company"} testid="settings-company">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Phone" value={company.phone} onChange={(v) => setC("phone", v)} testid="settings-company-phone" placeholder="876-555-0100" />
          <Field label="WhatsApp number" value={company.whatsapp} onChange={(v) => setC("whatsapp", v)} testid="settings-company-whatsapp" placeholder="876-555-0100" />
          <Field label="Email" value={company.email} onChange={(v) => setC("email", v)} testid="settings-company-email" />
          <Field label="Service area (hero badge)" value={company.service_area} onChange={(v) => setC("service_area", v)} testid="settings-company-area" />
          <div className="sm:col-span-2"><Field label="Address" value={company.address} onChange={(v) => setC("address", v)} testid="settings-company-address" /></div>
          <div className="sm:col-span-2"><Field label="Tagline (footer)" value={company.tagline} onChange={(v) => setC("tagline", v)} testid="settings-company-tagline" /></div>
        </div>
        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-slate-500">Delivery hours</Label>
            <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid="settings-hours-add" onClick={() => setC("hours", [...company.hours, { day: "", time: "" }])}><Plus className="w-3.5 h-3.5 mr-1" /> Add row</Button>
          </div>
          <div className="space-y-2">
            {company.hours.map((h, i) => (
              <div key={i} className="flex gap-2">
                <Input value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} placeholder="Mon – Fri" className="h-9 rounded-lg" data-testid={`settings-hours-day-${i}`} />
                <Input value={h.time} onChange={(e) => setHour(i, "time", e.target.value)} placeholder="8:00am – 6:00pm" className="h-9 rounded-lg" data-testid={`settings-hours-time-${i}`} />
                <Button size="icon" variant="ghost" className="h-9 w-9 text-rose-500 shrink-0" onClick={() => setC("hours", company.hours.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-5">
          <Label className="text-xs text-slate-500">Social links (leave blank to hide)</Label>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {["instagram", "facebook", "tiktok", "twitter"].map((s) => (
              <Field key={s} label={s[0].toUpperCase() + s.slice(1)} value={company.socials?.[s]} onChange={(v) => setSocial(s, v)} testid={`settings-social-${s}`} placeholder={`https://${s}.com/coolwaves`} />
            ))}
          </div>
        </div>
      </Card>

      <div className="space-y-6">
        <Card icon={Banknote} title="Bank Transfer Details" desc="Shown to customers who choose bank transfer." onSave={() => save("bank_details", bank)} saving={saving === "bank_details"} testid="settings-bank">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Field label="Bank name" value={bank.bank_name} onChange={(v) => setBank({ ...bank, bank_name: v })} testid="settings-bank-name" /></div>
            <Field label="Account name" value={bank.account_name} onChange={(v) => setBank({ ...bank, account_name: v })} testid="settings-bank-account-name" />
            <Field label="Account number" value={bank.account_number} onChange={(v) => setBank({ ...bank, account_number: v })} testid="settings-bank-account-number" />
            <Field label="Account type" value={bank.account_type} onChange={(v) => setBank({ ...bank, account_type: v })} testid="settings-bank-account-type" />
            <Field label="Branch" value={bank.branch} onChange={(v) => setBank({ ...bank, branch: v })} testid="settings-bank-branch" />
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-xs text-slate-500">Payment instructions</Label>
              <Textarea value={bank.instructions || ""} onChange={(e) => setBank({ ...bank, instructions: e.target.value })} rows={3} className="rounded-xl" data-testid="settings-bank-instructions" />
            </div>
          </div>
        </Card>

        <Card icon={Bell} title="Order Alerts" desc="Automatic notifications when a new order comes in." onSave={() => save("alerts", alerts)} saving={saving === "alerts"} testid="settings-alerts">
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div><div className="text-sm font-semibold text-slate-800">Email alerts</div><div className="text-xs text-slate-500">Admin gets every order; customers get a receipt if they enter an email.</div></div>
              <Switch checked={!!alerts.email_enabled} onCheckedChange={(v) => setAlerts({ ...alerts, email_enabled: v })} data-testid="settings-alerts-email-toggle" />
            </div>
            <Field label="Admin alert email" value={alerts.admin_email} onChange={(v) => setAlerts({ ...alerts, admin_email: v })} testid="settings-alerts-admin-email" type="email" />
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div><div className="text-sm font-semibold text-slate-800">WhatsApp alerts</div><div className="text-xs text-slate-500">Requires Twilio WhatsApp credentials configured on the server.</div></div>
              <Switch checked={!!alerts.whatsapp_enabled} onCheckedChange={(v) => setAlerts({ ...alerts, whatsapp_enabled: v })} data-testid="settings-alerts-whatsapp-toggle" />
            </div>
            <Field label="Admin WhatsApp number" value={alerts.admin_whatsapp} onChange={(v) => setAlerts({ ...alerts, admin_whatsapp: v })} testid="settings-alerts-admin-whatsapp" placeholder="876-555-0100" />
            <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-800">Also WhatsApp the customer</div>
              <Switch checked={!!alerts.whatsapp_customer} onCheckedChange={(v) => setAlerts({ ...alerts, whatsapp_customer: v })} data-testid="settings-alerts-whatsapp-customer" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
